import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getJSectionVO,
    saveJSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    J0_PAGE,
    J1_PAGE,
    J_1_PAGE,
    J_2_PAGE,
    J_PAGE_TITLES,
    J_SECTION_PAGES,
    JBreakdownPage,
    J1Page,
    JProgrammePage,
    JSectionPageCode,
} from '@/pages/components/audit-program/j-section/constants';
import J1SummaryView from '@/pages/components/audit-program/j-section/J1SummaryView';
import JBreakdownView from '@/pages/components/audit-program/j-section/JBreakdownView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type JSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: JSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function JSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'J0',
}: JSectionViewProps) {
    const [pageKey, setPageKey] = useState<JSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('J');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getJSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of J_SECTION_PAGES) {
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load J Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: JSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveJSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const j0Content = (getContent('J0') ?? J0_PAGE) as JProgrammePage;
    const j1Content = (getContent('J1') ?? J1_PAGE) as J1Page;
    const j1uContent = (getContent('J_1') ?? J_1_PAGE) as JBreakdownPage;
    const j2Content = (getContent('J_2') ?? J_2_PAGE) as JBreakdownPage;

    const tabs = useMemo(() => J_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={J_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'J0' ? (
                <G0ProgrammeView
                    header={header}
                    page={j0Content}
                    refCode="J0"
                    values={pageData.J0 ?? {}}
                    onChange={(k, v) => handleFieldChange('J0', k, v)}
                />
            ) : code === 'J1' ? (
                <J1SummaryView
                    header={header}
                    page={j1Content}
                    values={pageData.J1 ?? {}}
                    onChange={(k, v) => handleFieldChange('J1', k, v)}
                />
            ) : code === 'J_1' ? (
                <JBreakdownView
                    header={header}
                    page={j1uContent}
                    refCode="J_1"
                    values={pageData.J_1 ?? {}}
                    onChange={(k, v) => handleFieldChange('J_1', k, v)}
                />
            ) : (
                <JBreakdownView
                    header={header}
                    page={j2Content}
                    refCode="J_2"
                    values={pageData.J_2 ?? {}}
                    onChange={(k, v) => handleFieldChange('J_2', k, v)}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, j0Content, j1Content, j1uContent, j2Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    J Section — Trade & other receivables
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={!dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as JSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
